import { lowerCamelToTitle } from "@/api/util";
import {
  MetaType,
  MetaTypesBase,
  PropertyMeta,
  TypeDefineType,
} from "@/autogen/common";
import { useCSVUtil } from "./useCSVUtil";
import {
  PaginationResultBase,
  PaginationResultDTO,
  PaginationResultDTOGeneric,
} from "@/api/dto/PaginatedResultsDTO";
import { PaginationResult } from "../../cbi-generator/generated/dto/PaginationResult";
import { SortOption } from "../../cbi-generator/generated/dto/SortOption";
import { SortOrder } from "../../cbi-generator/generated/dto/SortOrder";
import { PaginationProps } from "vuetify/lib/components/VDataTable/composables/paginate.mjs";
import { useUtility } from "./useUtility";
import { errorMessage } from "./generated/contracts";
import { useNotificationStore } from "@/stores/notification";
import type { CsvExportOptions } from "../../cbi-generator/generated/dto/CsvExportOptions";

// Matches plain numbers and "human readable" currency-formatted numbers, e.g.
// "1234", "-1234.5", "$1,234.56", "-$12". Rejects anything else (dates, free
// text, etc.) so those values aren't misinterpreted as numeric.
const numericLikeRegex = /^-?\$?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\$?\d+(\.\d+)?$/;

/**
 * Parses a value that may be a raw number or a currency-formatted display
 * string (e.g. table "human readable" columns like "$1,234.56") into a
 * number usable for numeric sorting/comparison. Returns undefined if the
 * value doesn't look like a number (so callers can fall back to date or
 * string comparison instead of treating it as NaN/0).
 */
export function parseNumericLike(value: any): number | undefined {
  if (typeof value === "number") return isNaN(value) ? undefined : value;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!numericLikeRegex.test(trimmed)) return undefined;
  const cleaned = trimmed.replace(/[$,]/g, "");
  const parsed = Number(cleaned);
  return isNaN(parsed) ? undefined : parsed;
}

export type ItemType = Record<string, any> & { childTables?: ChildTablesType };

export type SortByType = {
  key: string;
  order: "desc" | "asc";
};

/** Converts Vuetify's own `sortBy` shape (`{key, order}[]`, `order` a plain
 * `"asc"`/`"desc"` string) into the CBIGen-generated `SortOption[]` every new-system
 * `{Resource}{Mode}SearchParams` DTO carries on its own `sort` field (see
 * `TypeScriptDtoEmitter.writeSortTypes`) - the shape backend search endpoints actually
 * understand. Used by [useTableHelper]'s `useServerSort` path. */
export function sortByToSortOptions(sortBy: SortByType[]): SortOption[] {
  return sortBy.map((s) => ({
    fieldName: s.key,
    order: s.order === "desc" ? SortOrder.DESC : SortOrder.ASC,
  }));
}

/** Builds a blank new-system `{Resource}{Mode}SearchParams` object (every
 * `*Exact`/`*AnyOf`/`*Like`/`*Less(Eq)`/`*Greater(Eq)` filter field defaulted to `null`,
 * i.e. "no filter on this field") straight off that DTO's generated `Meta` - since those
 * DTOs declare every field as required-but-nullable rather than optional, a plain
 * `{}` literal doesn't structurally satisfy them. `sort` isn't part of `meta.properties`
 * (it isn't a plain-scalar/enum field `MetaTypesBase` covers) so it's always added
 * separately by the caller. Meant for `useTableHelper`'s `defaultFilter`. */
export function emptySearchParams<T>(meta: MetaType<TypeDefineType>): T {
  const result: Record<string, any> = {};
  for (const property of meta.properties) {
    result[property.propertyName] = null;
  }
  return result as T;
}

/** Normalized pagination summary any pipeline (old OpenAPI-templated
 * `PaginationResultDTOGeneric`/`limit`+`offset`, or new CBIGen `PaginationResult`/
 * `page`+`perPage`) can be reduced to - lets `ReportingTableContents.ts` read a single
 * shape regardless of which `TableHelperOptions` loader a given page uses. */
export type PaginationInfo = {
  page: number;
  perPage: number;
  totalCount: number;
};

function paginationInfoFromLegacy(
  result: PaginationResultBase,
): PaginationInfo {
  return {
    page: result.limit > 0 ? Math.floor(result.offset / result.limit) + 1 : 1,
    perPage: result.limit,
    totalCount: result.totalRecords,
  };
}

function paginationInfoFromNew(result: PaginationResult<any>): PaginationInfo {
  return {
    page: result.page,
    perPage: result.perPage,
    totalCount: result.totalCount,
  };
}

export type HeaderType<T_Item extends ItemType> = {
  key: string;
  title: string;
  sortable?: boolean;
  meta?: PropertyMeta<any>;
  skipCSV?: boolean;
  /** Generated raw fields represented by a computed display column in server CSV. */
  csvFields?: string[];
  computeValue?: (
    rawValue: T_Item,
    forCSV: boolean,
  ) => string | null | undefined;
  computeValueColor?: (rawValue: T_Item) => string;
  renderer?: any;
};

export type FilterBase = Record<string, any>;

export type LoadItemsResult<T_Item extends ItemType> = {};

export type TableType<
  T_Item extends ItemType,
  T_Meta extends MetaType<TypeDefineType>,
> = {
  isLoading: Ref<Boolean>;
  meta?: T_Meta;
  items: Ref<T_Item[]>;
  itemValue: string;
  id: string;
  allowExport?: boolean;
  headersFiltered: ComputedRef<HeaderType<T_Item>[]>;
  footerItem?: Ref<T_Item | undefined>;
  expandedRows?: Ref<any[]>;
};

export type ChildTableType = TableType<any, any> & {
  isOpen: Ref<boolean>;
};

export type ChildTablesType = ChildTableType[];

export type TableHelperType<
  T_Item extends ItemType,
  T_Filter extends FilterBase,
  T_Meta extends MetaType<TypeDefineType>,
> = TableType<T_Item, T_Meta> & {
  filter: Ref<T_Filter>;
  sortBy: Ref<SortByType[]>;
  sortedItems: ComputedRef<T_Item[]>;
  headersRaw: ComputedRef<HeaderType<T_Item>[]>;
  extraHeaders: Ref<HeaderType<T_Item>[]>;
  visibleHeaders: Ref<string[]>;
  pagination: Ref<T_Pagination>;
  paginationResult: Ref<
    PaginationResultDTOGeneric<T_Item> | PaginationResult<T_Item> | null
  >;
  paginationInfo: ComputedRef<PaginationInfo | null>;
  reloadItems: () => Promise<void>;
  resetVisibleHeaders: () => void;
  isExporting: Ref<boolean>;
  saveCSVFile: (targetId?: string, scope?: "page" | "full") => Promise<void>;
};

export type T_Pagination = {
  page: number;
  perPage: number;
};

export type TableHelperOptions<
  T_Item extends ItemType,
  T_Filter extends FilterBase,
  T_Meta extends MetaType<TypeDefineType>,
> = {
  loadItems?: (
    filter: T_Filter,
  ) => Promise<{ items: T_Item[]; footer?: T_Item }>;
  loadItemsPaginated?: (
    pagination: T_Pagination,
    filter?: T_Filter,
  ) => Promise<{ items: PaginationResultDTOGeneric<T_Item>; footer?: T_Item }>;
  /** New-system equivalent of `loadItemsPaginated` - calls straight into a
   * CBIGen-generated `search{Resource}{Mode}(params, page, perPage)` service function
   * (or a thin wrapper around it) and returns its `PaginationResult<T_Item>` directly,
   * rather than the old OpenAPI-templated `{items: PaginationResultDTOGeneric}` wrapper.
   * When `useServerSort` is set, `sortBy` is merged onto `filter.sort` (as
   * `SortOption[]`) before each call so sorting happens server-side. */
  loadItemsServerPaginated?: (
    pagination: T_Pagination,
    filter: T_Filter,
    /** Export reads must reject failures instead of returning stale display rows. */
    exporting?: boolean,
  ) => Promise<PaginationResult<T_Item>>;
  /** When true, `sortBy` changes are pushed onto `filter.sort` (as `SortOption[]`) and
   * trigger a `reloadItems()` through `loadItemsServerPaginated`, instead of the default
   * client-side `sortedItems` re-sort. Only meaningful alongside `loadItemsServerPaginated`. */
  useServerSort?: boolean;
  id: string;
  allowExport?: boolean;
  meta?: T_Meta;
  defaultFilter?: T_Filter;
  defaultSortBy?: SortByType[];
  showByDefault?: string[];
  hideByDefault?: string[];
  alwaysHide?: string[];
  extraHeaders?: HeaderType<T_Item>[];
  headerOrder?: Record<string, number>;
  itemValue: string;
  getFileName?: (filter: T_Filter) => string;
  defaultPagination?: T_Pagination;
  watchFilter?: boolean;
  /** New-system equivalent of the client-side CSV building `saveCSVFile` otherwise does -
   * calls straight into a generated `export{Resource}{Mode}SearchToCsv(params, options)` service
   * function, which streams every matching row (not just the currently-loaded page) and
   * returns the finished CSV text directly for full exports. Page exports use the
   * displayed rows. Without this endpoint, full exports fetch all matching pages. */
  exportServerCsv?: (
    filter: T_Filter,
    columns: CsvExportOptions,
  ) => Promise<string>;
  /** Complete server CSV keys, including dataset-specific dynamic dimensions. */
  csvExportFields?: () => string[];
};

const { formatDateNY, formatTimeNY, formatRawCost } = useUtility();

const csvUtil = useCSVUtil();

export const useTableHelper = <
  T_Item extends ItemType,
  T_Filter extends FilterBase,
  T_Meta extends MetaType<TypeDefineType>,
>(
  options: TableHelperOptions<T_Item, T_Filter, T_Meta>,
) => {
  const sortBy = ref(options.defaultSortBy || []);

  const filter = ref<T_Filter | undefined>(options.defaultFilter);

  const items = ref<T_Item[]>([]);

  const pagination = ref<T_Pagination>(
    options.defaultPagination || { page: 1, perPage: 20 },
  );

  const paginationResult = ref<
    PaginationResultDTOGeneric<T_Item> | PaginationResult<T_Item> | null
  >(null);

  const paginationInfo = computed<PaginationInfo | null>(() => {
    const result = paginationResult.value;
    if (result == null) return null;
    if ("records" in result) {
      return paginationInfoFromLegacy(result);
    }
    return paginationInfoFromNew(result);
  });

  const footerItem = ref<T_Item>();

  const isLoading = ref(false);
  const isExporting = ref(false);
  let reloadVersion = 0;

  const reloadItems = async () => {
    const requestVersion = ++reloadVersion;
    isLoading.value = true;
    if (options.loadItems) {
      const result = await options.loadItems(filter.value);
      items.value = result.items;
      if (result.footer) {
        footerItem.value = result.footer;
      }
    }
    if (options.loadItemsPaginated) {
      const result = await options.loadItemsPaginated(
        pagination.value,
        filter.value,
      );
      items.value = result.items.records;
      paginationResult.value = result.items;
      if (result.footer) {
        footerItem.value = result.footer;
      }
    }
    if (options.loadItemsServerPaginated) {
      const effectiveFilter = (
        options.useServerSort
          ? { ...filter.value, sort: sortByToSortOptions(sortBy.value) }
          : filter.value
      ) as T_Filter;
      try {
        const result = await options.loadItemsServerPaginated(
          pagination.value,
          effectiveFilter,
        );
        if (requestVersion !== reloadVersion) return;
        items.value = result.items;
        paginationResult.value = result;
      } finally {
        if (requestVersion === reloadVersion) isLoading.value = false;
      }
      return;
    }
    isLoading.value = false;
  };

  watch(
    [pagination, () => (options.useServerSort ? sortBy.value : null)],
    async () => {
      await reloadItems();
    },
    { deep: true, immediate: true },
  );

  watch(
    () => JSON.stringify(filter.value),
    async () => {
      if (options.watchFilter) {
        await reloadItems();
      }
    },
  );

  function getHeaderOrder(key: string): number {
    if (options.headerOrder) {
      return options.headerOrder[key] || 0;
    }
    return 0;
  }

  const extraHeaders = ref(options.extraHeaders || []);

  const headersRaw = computed(() => {
    if (!options.meta) {
      return extraHeaders.value;
    }
    let remainingHeaders = [...(extraHeaders.value || [])];
    return options.meta.properties
      .filter((p) => !options.alwaysHide?.find((b) => b == p.propertyName))
      .map((property) => {
        const existingHeader = remainingHeaders.find(
          (a) => a.key == property.propertyName,
        );
        if (existingHeader != null) {
          remainingHeaders = remainingHeaders.filter(
            (a) => a !== existingHeader,
          );
        }
        const resolvedHeader: HeaderType<T_Item> = {
          ...(existingHeader || {
            key: property.propertyName,
            title: lowerCamelToTitle(property.propertyName),
          }),
          meta: property,
        };
        if (
          (property.propertyType === MetaTypesBase.currency ||
            (property.propertyType === MetaTypesBase.number &&
              property.propertyName.endsWith("Cents"))) &&
          !resolvedHeader.computeValue
        ) {
          resolvedHeader.computeValue = (item: T_Item) => {
            const value = item[property.propertyName];
            return value == null ? null : formatRawCost(value);
          };
        }
        if (
          property.propertyType === MetaTypesBase.boolean &&
          !resolvedHeader.computeValue
        ) {
          resolvedHeader.computeValue = (item: T_Item) => {
            const value = item[property.propertyName];
            return value == null ? null : value ? "Yes" : "No";
          };
        }
        return resolvedHeader;
      })
      .concat(remainingHeaders)
      .sort((a, b) => getHeaderOrder(b.key) - getHeaderOrder(a.key));
  });

  const defaultVisibleHeaders = () => {
    return headersRaw.value
      .filter((header) => {
        if (options.showByDefault != null)
          return options.showByDefault.find((a) => a == header.key);
        if (options.hideByDefault != null)
          return !options.hideByDefault.includes(header.key);
        return true;
      })
      .map((header) => header.key);
  };

  const visibleHeaders = ref<string[]>(defaultVisibleHeaders());

  const resetVisibleHeaders = () => {
    visibleHeaders.value = defaultVisibleHeaders();
  };

  function compareValues(a: any, b: any, key: string) {
    // Custom order
    /*if (customOrderMap[key]) {
            return (
            customOrderMap[key].indexOf(a[key]) -
            customOrderMap[key].indexOf(b[key])
            )
        }*/

    // Numbers (including currency-formatted "human readable" strings like "$1,234.56")
    const aNum = parseNumericLike(a[key]);
    const bNum = parseNumericLike(b[key]);
    if (aNum !== undefined && bNum !== undefined) {
      return aNum - bNum;
    }

    // Dates
    if (Date.parse(a[key]) && Date.parse(b[key])) {
      return new Date(a[key]).getTime() - new Date(b[key]).getTime();
    }

    // Strings (default)
    return String(a[key]).localeCompare(String(b[key]));
  }

  const sortedItems = computed(() => {
    if (options.useServerSort) return items.value;
    if (!sortBy.value.length) return items.value;

    return sortItems(items.value);
  });

  function sortItems<T>(rows: T[]) {
    return [...rows].sort((a, b) => {
      for (const sort of sortBy.value) {
        const result = compareValues(a, b, sort.key);

        if (result !== 0) {
          return sort.order === "desc" ? -result : result;
        }
      }
      return 0;
    });
  }

  const headersFiltered = computed(() => {
    return headersRaw.value.filter((header) => {
      return visibleHeaders.value.includes(header.key);
    });
  });

  const saveCSVFile = async (
    targetId?: string,
    scope: "page" | "full" = "full",
  ) => {
    if (isExporting.value) return;
    isExporting.value = true;
    try {
      await exportCSV(targetId, scope);
    } catch (cause) {
      useNotificationStore().addSnackbar(errorMessage(cause), "error");
    } finally {
      isExporting.value = false;
    }
  };

  const exportCSV = async (
    targetId: string | undefined,
    scope: "page" | "full",
  ) => {
    const getFileName = options.getFileName
      ? options.getFileName
      : (filter: T_Filter) => {
          let filterParams = "";
          if (
            filter.createdOnStart == filter.createdOnEnd &&
            filter.createdOnStart
          ) {
            filterParams = "_(On_" + formatDateNY(filter.createdOnStart) + ")";
          } else if (filter.createdOnStart && filter.createdOnEnd) {
            filterParams =
              "_(" +
              formatDateNY(filter.createdOnStart) +
              "_TO_" +
              formatDateNY(filter.createdOnEnd) +
              ")";
          } else if (filter.createdOnStart) {
            filterParams =
              "_(After_" + formatDateNY(filter.createdOnStart) + ")";
          } else if (filter.createdOnStart) {
            filterParams =
              "_(Before_" + formatDateNY(filter.createdOnEnd) + ")";
          }
          const fileName = (
            (targetId || options.id) +
            filterParams +
            "_ON_" +
            formatDateNY(new Date()) +
            "_AT_" +
            formatTimeNY(new Date()) +
            ".csv"
          )
            .replaceAll(" ", "_")
            .replaceAll("/", "_");
          return fileName;
        };
    const fileName = getFileName(filter.value);

    const effectiveFilter = (
      options.useServerSort
        ? { ...filter.value, sort: sortByToSortOptions(sortBy.value) }
        : { ...filter.value }
    ) as T_Filter;
    if (scope === "full" && targetId == null && options.exportServerCsv) {
      const fields =
        options.csvExportFields?.() ??
        options.meta?.properties.map((property) => property.propertyName) ??
        [];
      const fieldSet = new Set(fields);
      const columnOrder = [
        ...new Set(
          headersFiltered.value
            .filter((header) => !header.skipCSV)
            .flatMap((header) => header.csvFields ?? [header.key])
            .filter((field) => fieldSet.has(field)),
        ),
      ];
      const hideFields = fields.filter((field) => !columnOrder.includes(field));
      const csvString = await options.exportServerCsv(effectiveFilter, {
        columnOrder,
        hideFields,
      });
      csvUtil.downloadCSVString(csvString, fileName);
      return;
    }

    let headersToUse = headersFiltered.value;
    let sourceItems: (T_Item | (typeof sortedItems.value)[number])[] =
      sortedItems.value;
    const paginated =
      options.loadItemsServerPaginated || options.loadItemsPaginated;
    let exportFooter = footerItem.value;
    if (scope === "page" && !paginated && pagination.value.perPage > 0) {
      const start = (pagination.value.page - 1) * pagination.value.perPage;
      sourceItems = sourceItems.slice(start, start + pagination.value.perPage);
    } else if (scope === "full" && paginated) {
      const allItems: T_Item[] = [];
      const perPage =
        pagination.value.perPage > 0 ? pagination.value.perPage : 100;
      let totalCount = Infinity;
      for (let page = 1; allItems.length < totalCount; page++) {
        let pageItems: T_Item[];
        if (options.loadItemsServerPaginated) {
          const result = await options.loadItemsServerPaginated(
            { page, perPage },
            effectiveFilter,
            true,
          );
          if (result.page !== page)
            throw new Error(
              "The full export received a different page than requested. Please try again.",
            );
          pageItems = result.items;
          totalCount = result.totalCount;
        } else if (options.loadItemsPaginated) {
          const result = await options.loadItemsPaginated(
            { page, perPage },
            effectiveFilter,
          );
          pageItems = result.items.records;
          totalCount = result.items.totalRecords;
          exportFooter = result.footer;
        } else {
          throw new Error("No paginated export loader is available.");
        }
        if (!pageItems.length && allItems.length < totalCount)
          throw new Error(
            "The full export could not load all matching rows. Please try again.",
          );
        allItems.push(...pageItems);
      }
      sourceItems = options.useServerSort ? allItems : sortItems(allItems);
    }

    const getItemsToExport = () => {
      let toExport: any[] = [];
      if (targetId) {
        sourceItems.forEach((a) => {
          const itemValue = a[options.itemValue];
          a.childTables?.forEach((b: ChildTableType) => {
            if (b.id == targetId) {
              const itemsToAdd = (b as any).items || b.items.value;
              toExport = toExport.concat(
                itemsToAdd.map((d: any) => ({
                  ...d,
                  baseReport: itemValue.toString(),
                })),
              );
              headersToUse =
                (b as any).headersFiltered || b.headersFiltered.value;
            }
          });
        });
      } else {
        toExport = sourceItems.concat(exportFooter ? [exportFooter] : []);
      }
      return toExport;
    };

    // Use computeValue for CSV export if available
    const itemsToExport = getItemsToExport();

    if (targetId != null) {
      headersToUse = [
        {
          title: options.id,
          key: "baseReport",
        },
      ].concat(headersToUse);
    }

    const csvItems = itemsToExport.map((item: any) => {
      const csvItem: any = {};
      headersToUse.forEach((header: HeaderType<T_Item>) => {
        if (!header.skipCSV) {
          if (header.computeValue && item) {
            csvItem[header.key] = header.computeValue(item, true);
          } else {
            if (item) csvItem[header.key] = item[header.key];
          }
        }
      });
      return csvItem;
    });

    csvUtil.generateCSV(
      csvItems,
      fileName,
      headersToUse
        .filter((a) => !a.skipCSV)
        .map((a) => ({ title: a.title, value: a.key })),
    );
  };

  const expandedRows = ref<any[]>([]);

  const tableHelper: TableHelperType<T_Item, T_Filter, T_Meta> = {
    id: options.id,
    allowExport: options.allowExport,
    meta: options.meta,
    isLoading,
    sortBy,
    filter: filter as any,
    items: items as any,
    sortedItems: sortedItems as any,
    footerItem,
    headersRaw,
    visibleHeaders,
    headersFiltered,
    extraHeaders,
    pagination,
    paginationResult: paginationResult as any,
    paginationInfo,
    itemValue: options.itemValue,
    expandedRows,
    isExporting,
    saveCSVFile,
    reloadItems,
    resetVisibleHeaders,
  };
  return tableHelper;
};
